import React, { useContext, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { RoomContext } from '../context/RoomContext';
import ScrollToTop from '../components/ScrollToTop';
import { FaMapMarkerAlt, FaWifi, FaCoffee, FaParking, FaUtensils, FaGlassCheers } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import Reviews from '../pages/Reviews';
import { HOTEL_ADDRESS, HOTEL_LOCATION_URL, HOTEL_NAME } from '../config/hotel';
import API_BASE_URL from '../api';

const RoomDetails = () => {
  const { rooms, selectedRoomDetails, setSelectedRoomDetails } = useContext(RoomContext);
  const { id } = useParams();
  const navigate = useNavigate();
  
  const room = rooms.find((room) => room.id === Number(id));
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  if (!room) {
    return <div>Room not found</div>;
  }

  const handleNavigation = async () => {
    const checkInDate = selectedRoomDetails.checkInDate;
    const checkOutDate = selectedRoomDetails.checkOutDate;

    if (!checkInDate || !checkOutDate) {
      alert('Please select check-in and check-out dates first.');
      navigate('/rooms');
      return;
    }

    setCheckingAvailability(true);

    try {
      const totalGuests =
        Number(selectedRoomDetails.adults || 0) + Number(selectedRoomDetails.kids || 0);
      const response = await axios.get(`${API_BASE_URL}/inventory/available`, {
        params: {
          checkIn: checkInDate,
          checkOut: checkOutDate,
          maxPerson: totalGuests || 1,
          _: Date.now(),
        },
        timeout: 15000,
      });
      const isStillAvailable = (response.data || []).some(
        (availableRoom) => String(availableRoom.id) === String(room.id)
      );

      if (!isStillAvailable) {
        alert('This room is no longer available for the selected dates. Please choose another room.');
        navigate('/rooms');
        return;
      }
    } catch (error) {
      console.error('Error checking room availability:', error);
      alert('Could not confirm room availability. Please try again.');
      return;
    } finally {
      setCheckingAvailability(false);
    }

    const numberOfDays = selectedRoomDetails.numberOfDays || 1;
    const roomPrice = room.price * numberOfDays;
    const taxes = Math.round(roomPrice * 0.12);
    const totalAmount = roomPrice + taxes;

    setSelectedRoomDetails((prevDetails) => ({
      ...prevDetails,
      roomId: room.id,
      roomType: room.name,
      price: roomPrice,
      mealPlan: 'CP',
      mealCost: 0,
      taxes,
      totalAmount,
      adults: selectedRoomDetails.adults,
      kids: selectedRoomDetails.kids,
    }));
    navigate('/customerdetails');
  };

  return (
    <article>
      <Helmet>
        <title>{room.name} | {HOTEL_NAME}</title>
        <meta name="description" content={`Details and booking for ${room.name}. Enjoy luxury and comfort with our top-notch amenities.`} />
      </Helmet>
      <ScrollToTop />
      <header className="bg-room bg-cover bg-center h-[560px] relative flex justify-center items-center">
        <div className="absolute w-full h-full bg-black/70"></div>
        <h1 className="text-4xl md:text-6xl text-white z-20 font-primary text-center">
          {room.name}
        </h1>
      </header>
      <section className="container mx-auto px-4 md:px-0">
        <div className="flex flex-col lg:flex-row h-full py-12 lg:py-24">
          <div className="w-full lg:w-[60%] px-4 md:px-6">
            <h2 className="h2 text-3xl md:text-4xl">{room.name}</h2>
            <p className="mb-8 text-lg">{room.description}</p>  
            <section>
              <h3 className="h3 text-2xl mt-8">Hotel Rules</h3>
              <ul className="mb-6 list-disc list-inside text-lg">
                <li>Quiet hours are observed from 10 PM to 7 AM</li>
                <li>Visitors must depart by 10 PM.</li>
                <li>Please maintain the room in good condition</li>
                <li>Use the in-room safe for storing valuables</li>
                <li>Contact the front desk in case of emergencies</li>
              </ul>
            </section>
            <section>
              <h3 className="h3 text-2xl mt-8">Room Facilities</h3>
              <ul className="mb-6 list-none text-lg">
                <li className="flex items-center">
                  <FaWifi className="text-blue-500 mr-2" /> Wifi
                </li>
                <li className="flex items-center">
                  <FaCoffee className="text-yellow-500 mr-2" /> Coffee
                </li>
                <li className="flex items-center">
                  <FaParking className="text-green-500 mr-2" /> Parking Space
                </li>
                <li className="flex items-center">
                  <FaUtensils className="text-orange-500 mr-2" /> Breakfast
                </li>
                <li className="flex items-center">
                  <FaGlassCheers className="text-purple-500 mr-2" /> Drinks
                </li>
              </ul>
            </section>
          </div>
          <aside className="w-full lg:w-[40%] px-4 md:px-6">
            <ReservationSummary
              room={room}
              handleNavigation={handleNavigation}
              checkingAvailability={checkingAvailability}
            />
          </aside>
          
        </div>
        
       </section>
       <section className="bg-gray-10 mb-10">
        <div className="container mx-auto px-4">
         
           <Reviews />
        </div>
       </section>

    </article>
  );
};

const ReservationSummary = ({ room, handleNavigation, checkingAvailability }) => {
  const { selectedRoomDetails } = useContext(RoomContext);
  const roomPrice = room.price * selectedRoomDetails.numberOfDays;
  const taxes = Math.round(roomPrice * 0.12);

  return (
    <div className="p-4 border rounded shadow-lg bg-white">
      <h3 className="text-xl font-semibold">{room.name}</h3>
      <div className="my-4">
        <div className="grid grid-cols-2 gap-2">
          <p><strong>Check-In:</strong> {selectedRoomDetails.checkInDate || "N/A"}</p>
          <p><strong>Check-Out:</strong> {selectedRoomDetails.checkOutDate || "N/A"}</p>
          <p><strong>Adults:</strong> {selectedRoomDetails.adults}</p>
          <p><strong>Kids:</strong> {selectedRoomDetails.kids}</p>
        </div>
      </div>

      <p className="mb-4 text-gray-700">complementory breackfast</p>

      <ul className="list-disc pl-5">
        <li className="text-green-600">Free Cancellation till check-in</li>
        <li className="text-blue-500">Couples are allowed at our Property</li>
      </ul>

      <p className="text-2xl font-bold">
        Rs. {roomPrice} + Rs. {taxes} taxes & fees
      </p>
      
      <button
        className="btn btn-lg btn-primary w-full bg-blue-500 text-white font-bold py-2 px-4 rounded hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
        onClick={handleNavigation}
        disabled={checkingAvailability}
      >
        {checkingAvailability ? 'CHECKING...' : 'BOOK THIS NOW'}
      </button>
      
      <div className="mt-4 border-t pt-4">
        <div className="flex items-center">
          <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
            <p className="text-lg font-bold text-blue-500">3.9</p>
          </div>
          <div className="ml-4">
            <p className="font-bold text-blue-700">Very Good</p>
          </div>
        </div>
        
        <div className="flex items-center mt-4">
          <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
            <FaMapMarkerAlt className="text-blue-500" />
          </div>
          <div className="ml-4">
            <a
              href={HOTEL_LOCATION_URL}
              className="flex items-center"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View on Google Maps"
            >
              <span>{HOTEL_ADDRESS}</span>
            </a>
            <p className="text-sm text-blue-500 cursor-pointer">
              <a
                href={HOTEL_LOCATION_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                See on Map
              </a>
            </p>
          </div>
        </div>
      </div>
     
    </div>
  );
};

export default RoomDetails;
